import { FunctionComponent, SVGProps } from "react";
import { NavigateFunction } from "react-router-dom";
import ExpiredIcon from "~/assets/svg/expired_icon.svg?react";
import PastDueIcon from "~/assets/svg/past_due.svg?react";
import SubCancelIcon from "~/assets/svg/sub_cancel.svg?react";
import TrialEndIcon from "~/assets/svg/trial_end.svg?react";
import { paths } from "~/router/paths";
import { IAccount } from "~/types/account";
import { SubscriptionLockReason } from "./subscriptionExpired";

export type SubscriptionLockHandlers = {
  navigate: NavigateFunction;
  showIntercom: () => void;
};

export type SubscriptionLockConfig = {
  icon: FunctionComponent<SVGProps<SVGSVGElement>>;
  iconBackground: string;
  titleKey: string;
  descriptionKey: string;
  primaryCtaKey: string;
  secondaryCtaKey: string;
  onPrimary: (
    handlers: SubscriptionLockHandlers,
    account: IAccount | null
  ) => void;
  onSecondary: (
    handlers: SubscriptionLockHandlers,
    account: IAccount | null
  ) => void;
};

const navigateToCheckout = (
  navigate: NavigateFunction,
  account: IAccount | null
) => {
  const selectedPlanId = account?.last_subscription?.plan?.id || "";

  if (!selectedPlanId) {
    navigate(paths.settings.plans);
    return;
  }

  navigate(paths.checkout.index, {
    state: { selectedPlanId },
  });
};

export const SUBSCRIPTION_LOCK_CONFIG: Record<
  SubscriptionLockReason,
  SubscriptionLockConfig
> = {
  past_due: {
    icon: PastDueIcon,
    iconBackground: "#EB58601A",
    titleKey: "subscription_lock.past_due.title",
    descriptionKey: "subscription_lock.past_due.description",
    primaryCtaKey: "subscription_lock.past_due.update_payment_method",
    secondaryCtaKey: "subscription_lock.past_due.try_again",
    onPrimary: ({ navigate }) => navigate(paths.settings.account),
    onSecondary: ({ navigate }, account) =>
      navigateToCheckout(navigate, account),
  },
  trial_ended: {
    icon: TrialEndIcon,
    iconBackground: "#F3F3FF",
    titleKey: "subscription_lock.trial_ended.title",
    descriptionKey: "subscription_lock.trial_ended.description",
    primaryCtaKey: "subscription_lock.trial_ended.upgrade_now",
    secondaryCtaKey: "subscription_lock.trial_ended.view_plans",
    onPrimary: ({ navigate }, account) => navigateToCheckout(navigate, account),
    onSecondary: ({ navigate }) => navigate(paths.settings.plans),
  },
  cancelled: {
    icon: SubCancelIcon,
    iconBackground: "#FEF3F2",
    titleKey: "subscription_lock.cancelled.title",
    descriptionKey: "subscription_lock.cancelled.description",
    primaryCtaKey: "subscription_lock.cancelled.reactivate_subscription",
    secondaryCtaKey: "subscription_lock.cancelled.contact_support",
    onPrimary: ({ navigate }, account) => navigateToCheckout(navigate, account),
    onSecondary: ({ showIntercom }) => showIntercom(),
  },
  expired: {
    icon: ExpiredIcon,
    iconBackground: "#FFFAEB",
    titleKey: "subscription_expired.title",
    descriptionKey: "subscription_expired.description",
    primaryCtaKey: "subscription_expired.renew_subscription",
    secondaryCtaKey: "subscription_expired.view_plans",
    onPrimary: ({ navigate }, account) => navigateToCheckout(navigate, account),
    onSecondary: ({ navigate }) => navigate(paths.settings.plans),
  },
};
